import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Header from '../components/Header';
import MobileActionBar from '../components/MobileActionBar';
import {
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  ArrowRight,
  Building2,
  Key,
  Wrench,
  Paintbrush,
  Hammer,
  Droplets,
  Zap,
  Clock,
  Shield,
  Home as HomeIcon,
  Layers,
  ChevronRight
} from 'lucide-react';
import { companyInfo } from '../data/mock';

// Location-specific data with genuinely differentiated content
export const locationData = {
  manchester: {
    name: 'Manchester',
    slug: 'manchester',
    region: 'Greater Manchester',
    metaTitle: 'Property Maintenance Manchester | Landlord Services | Expert Property Maintenance',
    metaDescription: 'Property maintenance in Manchester for landlords, letting agents and property managers. Reactive repairs, void works, refurbishment. Covering M1-M45 postcodes. Call 07300 405045.',
    canonicalUrl: 'https://expert-propertymaintenance.co.uk/property-maintenance-manchester',
    heroTitle: 'Property Maintenance in Manchester',
    heroSubtitle: 'Supporting landlords, letting agents and property managers across Manchester with reliable repairs, maintenance and refurbishment services.',
    introContent: `Manchester's diverse property market — from Victorian terraces in Chorlton to modern apartments in the Northern Quarter — requires maintenance providers who understand the specific needs of rental properties. Expert Property Maintenance works with landlords and agents across the city, handling everything from tenant-reported repairs to complete void turnarounds.`,
    propertyContext: `With a high concentration of rental properties serving young professionals, students and families, Manchester landlords need responsive maintenance support. We understand that minimising void periods and keeping tenants happy means addressing issues promptly and professionally.`,
    areas: ['City Centre', 'Ancoats', 'Northern Quarter', 'Chorlton', 'Fallowfield', 'Rusholme', 'Levenshulme', 'Gorton', 'Longsight', 'Hulme', 'Moss Side', 'Whalley Range'],
    postcodes: 'M1, M2, M3, M4, M11, M12, M13, M14, M15, M16, M19, M20, M21',
    nearbyLocations: [
      { name: 'Salford', slug: 'salford' },
      { name: 'Didsbury', slug: 'didsbury' },
      { name: 'Stockport', slug: 'stockport' }
    ],
    keyServices: [
      { title: 'Reactive Repairs', desc: 'Fast response to tenant-reported issues across Manchester rental properties', link: '/reactive-property-maintenance' },
      { title: 'End of Tenancy', desc: 'Preparing properties between tenants — repairs, decorating and general maintenance', link: '/end-of-tenancy' },
      { title: 'Void Works', desc: 'Complete void turnarounds for Manchester landlords and letting agents', link: '/void-property-works' }
    ],
    localBenefit: 'Centrally located to respond quickly to properties across Manchester and the surrounding areas.'
  },
  stockport: {
    name: 'Stockport',
    slug: 'stockport',
    region: 'Greater Manchester',
    metaTitle: 'Property Maintenance Stockport | Heaton Moor, Marple, Bramhall | Expert Property Maintenance',
    metaDescription: 'Property maintenance in Stockport for landlords and letting agents. Covering Heaton Moor, Marple, Bramhall, Hazel Grove. Repairs, decorating, refurbishment. Call 07300 405045.',
    canonicalUrl: 'https://expert-propertymaintenance.co.uk/property-maintenance-stockport',
    heroTitle: 'Property Maintenance in Stockport',
    heroSubtitle: 'Professional property maintenance for landlords and letting agents across Stockport — from the town centre to Marple and Bramhall.',
    introContent: `Stockport offers excellent rental yields with a mix of traditional terraced properties in areas like Edgeley and larger family homes in Bramhall and Marple. Expert Property Maintenance provides reliable maintenance support for landlords managing properties across the borough.`,
    propertyContext: `Many Stockport landlords manage older properties that require regular maintenance attention. Victorian and Edwardian homes in Heaton Moor and Heaton Chapel often need specialist plaster repairs, sash window attention and period-appropriate decorating.`,
    areas: ['Stockport Town Centre', 'Edgeley', 'Heaton Moor', 'Heaton Norris', 'Heaton Chapel', 'Reddish', 'Portwood', 'Brinnington', 'Bredbury', 'Marple', 'Hazel Grove', 'Bramhall'],
    postcodes: 'SK1, SK2, SK3, SK4, SK5, SK6, SK7, SK8',
    nearbyLocations: [
      { name: 'Cheadle', slug: 'cheadle' },
      { name: 'Didsbury', slug: 'didsbury' },
      { name: 'Manchester', slug: 'manchester' }
    ],
    keyServices: [
      { title: 'Plastering & Repairs', desc: 'Specialist plaster repairs for period properties in Heaton Moor and surrounding areas', link: '/plastering-repairs' },
      { title: 'Painting & Decorating', desc: 'Professional decorating for rental properties throughout Stockport', link: '/painting-decorating' },
      { title: 'Carpentry & Joinery', desc: 'Door repairs, skirting and joinery work for Stockport landlords', link: '/carpentry-joinery' }
    ],
    localBenefit: 'Regular work across Stockport means we know the area and its property types well.'
  },
  cheadle: {
    name: 'Cheadle',
    slug: 'cheadle',
    region: 'Greater Manchester',
    metaTitle: 'Property Maintenance Cheadle & Cheadle Hulme | Landlord Services | Expert Property Maintenance',
    metaDescription: 'Property maintenance in Cheadle and Cheadle Hulme for landlords. Covering Gatley, Heald Green. Repairs, decorating, carpentry. Call Expert Property Maintenance 07300 405045.',
    canonicalUrl: 'https://expert-propertymaintenance.co.uk/property-maintenance-cheadle',
    heroTitle: 'Property Maintenance in Cheadle',
    heroSubtitle: 'Trusted property maintenance services for landlords and property managers in Cheadle, Cheadle Hulme and surrounding areas.',
    introContent: `Cheadle and Cheadle Hulme are popular residential areas with strong rental demand from families and professionals working in Manchester and at the nearby airport. Expert Property Maintenance supports landlords with maintenance and repairs across the area.`,
    propertyContext: `Properties in Cheadle range from inter-war semis to modern developments. Landlords here often require maintenance that maintains property standards expected by discerning tenants in this sought-after area.`,
    areas: ['Cheadle', 'Cheadle Hulme', 'Gatley', 'Heald Green'],
    postcodes: 'SK8',
    nearbyLocations: [
      { name: 'Stockport', slug: 'stockport' },
      { name: 'Didsbury', slug: 'didsbury' },
      { name: 'Wilmslow', slug: 'wilmslow' }
    ],
    keyServices: [
      { title: 'Planned Maintenance', desc: 'Regular maintenance programmes for Cheadle landlords with multiple properties', link: '/planned-property-maintenance' },
      { title: 'Property Refurbishment', desc: 'Updating tired rental properties to maximise rental potential', link: '/property-refurbishment' },
      { title: 'Handyman Services', desc: 'General repairs and odd jobs for Cheadle rental properties', link: '/handyman-services' }
    ],
    localBenefit: 'Convenient location for properties in Cheadle, Gatley and the surrounding Stockport area.'
  },
  didsbury: {
    name: 'Didsbury',
    slug: 'didsbury',
    region: 'South Manchester',
    metaTitle: 'Property Maintenance Didsbury | West & East Didsbury | Expert Property Maintenance',
    metaDescription: 'Property maintenance in Didsbury for landlords and letting agents. Covering West Didsbury, East Didsbury, Withington. End of tenancy, repairs, decorating. Call 07300 405045.',
    canonicalUrl: 'https://expert-propertymaintenance.co.uk/property-maintenance-didsbury',
    heroTitle: 'Property Maintenance in Didsbury',
    heroSubtitle: 'Quality property maintenance for landlords and agents in Didsbury Village, West Didsbury and East Didsbury.',
    introContent: `Didsbury is one of South Manchester's most desirable rental areas, attracting young professionals and families with its village atmosphere, excellent schools and transport links. Expert Property Maintenance works with landlords and agents managing properties across this popular location.`,
    propertyContext: `Rental properties in Didsbury command premium rents, and tenants expect high standards. From Victorian terraces on Wilmslow Road to modern apartments near the Metrolink, maintaining property condition is essential for landlords in this competitive market.`,
    areas: ['Didsbury Village', 'West Didsbury', 'East Didsbury', 'Withington', 'Burnage'],
    postcodes: 'M20, M19',
    nearbyLocations: [
      { name: 'Stockport', slug: 'stockport' },
      { name: 'Cheadle', slug: 'cheadle' },
      { name: 'Manchester', slug: 'manchester' }
    ],
    keyServices: [
      { title: 'End of Tenancy Repairs', desc: 'Quick turnaround repairs to minimise void periods in Didsbury', link: '/end-of-tenancy' },
      { title: 'Painting & Decorating', desc: 'High-quality decorating for Didsbury rental properties', link: '/painting-decorating' },
      { title: 'Reactive Maintenance', desc: 'Prompt response to tenant issues in this competitive rental market', link: '/reactive-property-maintenance' }
    ],
    localBenefit: 'Regular presence in Didsbury and South Manchester means quick response times for your properties.'
  },
  wilmslow: {
    name: 'Wilmslow',
    slug: 'wilmslow',
    region: 'Cheshire',
    metaTitle: 'Property Maintenance Wilmslow & Alderley Edge | Cheshire | Expert Property Maintenance',
    metaDescription: 'Property maintenance in Wilmslow and Alderley Edge for landlords. Quality repairs, decorating, refurbishment for Cheshire rental properties. Call 07300 405045.',
    canonicalUrl: 'https://expert-propertymaintenance.co.uk/property-maintenance-wilmslow',
    heroTitle: 'Property Maintenance in Wilmslow',
    heroSubtitle: 'Professional property maintenance services for landlords and property managers in Wilmslow, Alderley Edge and surrounding Cheshire areas.',
    introContent: `Wilmslow and Alderley Edge are premium residential locations with rental properties ranging from apartments in the town centre to substantial family homes. Expert Property Maintenance provides the quality workmanship that landlords in this area expect.`,
    propertyContext: `Properties in Wilmslow attract executive tenants who expect excellent maintenance standards. Landlords need reliable contractors who can deliver quality work while representing their properties professionally.`,
    areas: ['Wilmslow', 'Alderley Edge', 'Handforth', 'Styal'],
    postcodes: 'SK9',
    nearbyLocations: [
      { name: 'Cheadle', slug: 'cheadle' },
      { name: 'Stockport', slug: 'stockport' },
      { name: 'Altrincham', slug: 'altrincham' }
    ],
    keyServices: [
      { title: 'Property Refurbishment', desc: 'Quality refurbishment for Wilmslow rental properties', link: '/property-refurbishment' },
      { title: 'Carpentry & Joinery', desc: 'Skilled carpentry work including built-in storage and joinery repairs', link: '/carpentry-joinery' },
      { title: 'Planned Maintenance', desc: 'Regular maintenance to keep premium properties in excellent condition', link: '/planned-property-maintenance' }
    ],
    localBenefit: 'Serving Cheshire alongside our Greater Manchester coverage.'
  },
  altrincham: {
    name: 'Altrincham',
    slug: 'altrincham',
    region: 'Trafford',
    metaTitle: 'Property Maintenance Altrincham | Hale, Bowdon, Timperley | Expert Property Maintenance',
    metaDescription: 'Property maintenance in Altrincham, Hale and Bowdon for landlords. Repairs, decorating, void works for Trafford rental properties. Call 07300 405045.',
    canonicalUrl: 'https://expert-propertymaintenance.co.uk/property-maintenance-altrincham',
    heroTitle: 'Property Maintenance in Altrincham',
    heroSubtitle: 'Reliable property maintenance for landlords and letting agents across Altrincham, Hale, Bowdon and Timperley.',
    introContent: `Altrincham has seen significant regeneration and remains one of Trafford's most desirable rental locations. Expert Property Maintenance works with landlords managing properties from town centre apartments to family homes in Hale and Bowdon.`,
    propertyContext: `The Altrincham area has a mix of modern apartments, Victorian terraces and larger detached properties. Landlords benefit from our multi-trade capability — handling decorating, carpentry, plumbing and electrical through a single point of contact.`,
    areas: ['Altrincham Town Centre', 'Hale', 'Bowdon', 'Timperley', 'Broadheath'],
    postcodes: 'WA14, WA15',
    nearbyLocations: [
      { name: 'Sale', slug: 'sale' },
      { name: 'Wilmslow', slug: 'wilmslow' },
      { name: 'Manchester', slug: 'manchester' }
    ],
    keyServices: [
      { title: 'Void Property Works', desc: 'Complete void turnarounds for Altrincham landlords', link: '/void-property-works' },
      { title: 'Handyman Services', desc: 'General repairs and maintenance across the Trafford area', link: '/handyman-services' },
      { title: 'Letting Agent Maintenance', desc: 'Reliable contractor support for Altrincham letting agencies', link: '/letting-agent-maintenance' }
    ],
    localBenefit: 'Good coverage of the Trafford area including Altrincham, Sale and surrounding locations.'
  },
  sale: {
    name: 'Sale',
    slug: 'sale',
    region: 'Trafford',
    metaTitle: 'Property Maintenance Sale | Trafford Landlord Services | Expert Property Maintenance',
    metaDescription: 'Property maintenance in Sale and Trafford for landlords. Reactive repairs, end of tenancy works, refurbishment. Covering Sale Moor, Ashton-on-Mersey. Call 07300 405045.',
    canonicalUrl: 'https://expert-propertymaintenance.co.uk/property-maintenance-sale',
    heroTitle: 'Property Maintenance in Sale',
    heroSubtitle: 'Property maintenance and repairs for landlords and agents in Sale, Sale Moor and the wider Trafford area.',
    introContent: `Sale offers good rental demand with excellent transport links to Manchester city centre. Expert Property Maintenance provides reliable maintenance support for landlords managing properties across the area.`,
    propertyContext: `With its mix of 1930s semis, Victorian terraces and newer developments, Sale presents varied maintenance requirements. We work with landlords of all sizes, from those with a single buy-to-let to agents managing larger portfolios.`,
    areas: ['Sale Town Centre', 'Sale Moor', 'Ashton-on-Mersey', 'Brooklands'],
    postcodes: 'M33',
    nearbyLocations: [
      { name: 'Altrincham', slug: 'altrincham' },
      { name: 'Manchester', slug: 'manchester' },
      { name: 'Salford', slug: 'salford' }
    ],
    keyServices: [
      { title: 'Reactive Maintenance', desc: 'Fast response to tenant-reported issues in Sale properties', link: '/reactive-property-maintenance' },
      { title: 'End of Tenancy Repairs', desc: 'Preparing properties for new tenants quickly and efficiently', link: '/end-of-tenancy' },
      { title: 'Painting & Decorating', desc: 'Professional decorating for Sale rental properties', link: '/painting-decorating' }
    ],
    localBenefit: 'Covering Sale alongside Altrincham and the wider Trafford area.'
  },
  salford: {
    name: 'Salford',
    slug: 'salford',
    region: 'Greater Manchester',
    metaTitle: 'Property Maintenance Salford | MediaCityUK, Salford Quays | Expert Property Maintenance',
    metaDescription: 'Property maintenance in Salford for landlords. Covering Salford Quays, MediaCityUK, Eccles, Swinton. Repairs, void works, refurbishment. Call 07300 405045.',
    canonicalUrl: 'https://expert-propertymaintenance.co.uk/property-maintenance-salford',
    heroTitle: 'Property Maintenance in Salford',
    heroSubtitle: 'Professional property maintenance services for landlords across Salford — from Salford Quays to Eccles, Swinton and Worsley.',
    introContent: `Salford's property landscape has transformed dramatically, with modern apartments at MediaCityUK and Salford Quays alongside traditional terraced housing in areas like Eccles and Swinton. Expert Property Maintenance works with landlords across all property types.`,
    propertyContext: `The rental market in Salford serves everyone from young professionals in waterside apartments to families in suburban terraces. We understand that different property types require different approaches — and landlords need reliable support regardless of their portfolio makeup.`,
    areas: ['Salford Quays', 'MediaCityUK', 'Eccles', 'Swinton', 'Walkden', 'Worsley', 'Irlam', 'Pendleton', 'Broughton'],
    postcodes: 'M5, M6, M7, M27, M28, M30, M38, M44, M50',
    nearbyLocations: [
      { name: 'Manchester', slug: 'manchester' },
      { name: 'Sale', slug: 'sale' },
      { name: 'Altrincham', slug: 'altrincham' }
    ],
    keyServices: [
      { title: 'Reactive Maintenance', desc: 'Quick response to repairs across Salford rental properties', link: '/reactive-property-maintenance' },
      { title: 'Void Property Works', desc: 'Preparing properties between tenancies throughout Salford', link: '/void-property-works' },
      { title: 'Property Refurbishment', desc: 'Refurbishing tired properties to maximise rental potential', link: '/property-refurbishment' }
    ],
    localBenefit: 'Strong presence across Greater Manchester including Salford and Manchester city centre.'
  }
};

const services = [
  { icon: Wrench, title: 'Reactive Maintenance', desc: 'Fast response to tenant issues', link: '/reactive-property-maintenance' },
  { icon: Clock, title: 'Planned Maintenance', desc: 'Scheduled preventative maintenance', link: '/planned-property-maintenance' },
  { icon: Key, title: 'End of Tenancy', desc: 'Quick turnaround repairs', link: '/end-of-tenancy' },
  { icon: HomeIcon, title: 'Void Works', desc: 'Complete void turnarounds', link: '/void-property-works' },
  { icon: Paintbrush, title: 'Decorating', desc: 'Painting and decorating', link: '/painting-decorating' },
  { icon: Hammer, title: 'Carpentry', desc: 'Doors, skirting, joinery', link: '/carpentry-joinery' },
  { icon: Layers, title: 'Plastering', desc: 'Plaster repairs and skimming', link: '/plastering-repairs' },
  { icon: Droplets, title: 'Plumbing', desc: 'General plumbing repairs', link: '#services' },
  { icon: Zap, title: 'Electrical', desc: 'Minor electrical works', link: '#services' }
];

const benefits = [
  'Single point of contact for all trades',
  'Multi-trade capability — carpentry, decorating, plastering, plumbing, electrical',
  'Understanding of rental property requirements',
  'Fast turnaround to minimise void periods',
  '£5 million public liability insurance',
  '12-month workmanship guarantee'
];

const LocationPage = ({ location }) => {
  const otherLocations = useMemo(
    () => Object.values(locationData).filter((loc) => loc.slug !== location),
    [location]
  );
  const data = locationData[location];
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    postcode: '',
    message: '',
    honeypot: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.honeypot) return; // Spam protection
    
    setIsSubmitting(true);
    setSubmitError(false);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          properties_managed: formData.postcode,
          job_description: `[${data.name} Area Enquiry] ${formData.message}`,
          trade_type: 'general',
          source_website: 'expert-propertymaintenance.co.uk',
          source_page: `/property-maintenance-${data.slug}`
        }),
      });
      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', phone: '', postcode: '', message: '', honeypot: '' });
      } else {
        setSubmitError(true);
      }
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!data) {
    return <div>Location not found</div>;
  }

  // Structured data for service area (NOT a physical location)
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Property Maintenance",
    "provider": {
      "@type": "HomeAndConstructionBusiness",
      "name": "Expert Property Maintenance",
      "telephone": "+447300405045",
      "email": "info@expert-propertymaintenance.co.uk",
      "url": "https://expert-propertymaintenance.co.uk"
    },
    "areaServed": {
      "@type": "City",
      "name": data.name,
      "containedInPlace": {
        "@type": "AdministrativeArea",
        "name": data.region
      }
    },
    "description": data.metaDescription
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{data.metaTitle}</title>
        <meta name="description" content={data.metaDescription} />
        <link rel="canonical" href={data.canonicalUrl} />
        <meta property="og:title" content={data.metaTitle} />
        <meta property="og:description" content={data.metaDescription} />
        <meta property="og:url" content={data.canonicalUrl} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <Header variant="default" companyPhone={companyInfo.phone} />

      {/* Hero */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-20 bg-gradient-to-b from-brand-navy to-brand-navy/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-brand-yellow/10 border border-brand-yellow/30 text-brand-yellow px-4 py-2 rounded-full text-sm font-medium mb-6">
              <MapPin className="w-4 h-4" />
              {data.region}
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              {data.heroTitle}
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              {data.heroSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold" asChild>
                <a href="#contact" data-testid="location-hero-cta">
                  Request a Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-brand-navy" asChild>
                <a href={`tel:${companyInfo.phone}`}>
                  <Phone className="w-5 h-5 mr-2" />
                  {companyInfo.phone}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section with Location-Specific Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-brand-navy mb-6">
                Property Maintenance Services in {data.name}
              </h2>
              <p className="text-lg text-brand-slate mb-6">
                {data.introContent}
              </p>
              <p className="text-brand-slate mb-8">
                {data.propertyContext}
              </p>
              <div className="space-y-3">
                {benefits.map((benefit) => (
                  <div key={benefit} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0 mt-0.5" />
                    <span className="text-brand-slate">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="bg-brand-grey rounded-2xl p-6">
                <h3 className="text-lg font-bold text-brand-navy mb-4">Areas We Cover in {data.name}</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {data.areas.map((area) => (
                    <span key={area} className="px-3 py-1.5 bg-white border border-slate-200 text-brand-slate rounded-full text-sm">
                      {area}
                    </span>
                  ))}
                </div>
                <p className="text-sm text-brand-slate/80">Postcodes: {data.postcodes}</p>
              </div>
              
              <div className="bg-brand-grey border border-brand-yellow/30 rounded-2xl p-6">
                <h4 className="font-semibold text-brand-navy mb-2">Nearby Areas</h4>
                <div className="flex flex-wrap gap-2">
                  {data.nearbyLocations.map((loc) => (
                    <a 
                      key={loc.slug}
                      href={`/property-maintenance-${loc.slug}`}
                      className="px-3 py-1.5 bg-white hover:bg-brand-grey border border-yellow-300 text-brand-slate hover:text-brand-navy rounded-full text-sm transition-colors"
                    >
                      {loc.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Services for this Area */}
      <section className="py-16 bg-brand-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-brand-navy mb-4 text-center">
            Key Services for {data.name} Landlords
          </h2>
          <p className="text-lg text-brand-slate text-center mb-12 max-w-2xl mx-auto">
            Services particularly relevant to landlords and letting agents in the {data.name} area.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {data.keyServices.map((service) => (
              <Card key={service.title} className="border-slate-200 hover:border-brand-yellow hover:shadow-lg transition-all bg-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-brand-navy mb-2">{service.title}</h3>
                  <p className="text-brand-slate text-sm mb-4">{service.desc}</p>
                  <a href={service.link} className="inline-flex items-center text-brand-yellow hover:text-yellow-300 font-medium text-sm">
                    Learn more <ChevronRight className="w-4 h-4 ml-1" />
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>

          <h3 className="text-xl font-bold text-brand-navy mb-6 text-center">All Services Available</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((service) => (
              <a 
                key={service.title} 
                href={service.link}
                className="flex items-center gap-4 p-4 bg-white border border-slate-200 rounded-xl hover:border-brand-yellow hover:shadow-md transition-all"
              >
                <div className="w-10 h-10 bg-brand-grey rounded-lg flex items-center justify-center flex-shrink-0">
                  <service.icon className="w-5 h-5 text-brand-yellow" />
                </div>
                <div>
                  <div className="font-semibold text-brand-navy">{service.title}</div>
                  <div className="text-sm text-brand-slate/80">{service.desc}</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* For Landlords */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-brand-navy rounded-2xl p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-brand-yellow/10 border border-brand-yellow/30 text-brand-yellow px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Building2 className="w-4 h-4" />
                  For Property Professionals
                </div>
                <h2 className="text-3xl font-bold text-white mb-4">
                  {data.name} Landlords & Letting Agents
                </h2>
                <p className="text-slate-300 mb-4">
                  Managing rental properties in {data.name}? Expert Property Maintenance provides the reliable 
                  maintenance support you need — from individual repairs to ongoing maintenance and refurbishments.
                </p>
                <p className="text-slate-400 text-sm mb-6">
                  {data.localBenefit}
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button size="lg" className="bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold" asChild>
                    <a href="/landlord-services">
                      Landlord Services
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </a>
                  </Button>
                  <Button size="lg" variant="outline" className="border-slate-600 text-white hover:bg-brand-navy/90" asChild>
                    <a href="/letting-agent-maintenance">
                      Letting Agent Services
                    </a>
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-800 rounded-xl p-5">
                  <Wrench className="w-8 h-8 text-brand-yellow mb-3" />
                  <h4 className="font-semibold text-white mb-1">Reactive Repairs</h4>
                  <p className="text-sm text-slate-400">Fast response to tenant issues</p>
                </div>
                <div className="bg-slate-800 rounded-xl p-5">
                  <Key className="w-8 h-8 text-brand-yellow mb-3" />
                  <h4 className="font-semibold text-white mb-1">Void Works</h4>
                  <p className="text-sm text-slate-400">Quick turnaround between lets</p>
                </div>
                <div className="bg-slate-800 rounded-xl p-5">
                  <Paintbrush className="w-8 h-8 text-brand-yellow mb-3" />
                  <h4 className="font-semibold text-white mb-1">Refurbishment</h4>
                  <p className="text-sm text-slate-400">Maximise rental value</p>
                </div>
                <div className="bg-slate-800 rounded-xl p-5">
                  <Shield className="w-8 h-8 text-brand-yellow mb-3" />
                  <h4 className="font-semibold text-white mb-1">Fully Insured</h4>
                  <p className="text-sm text-slate-400">£5m public liability</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-16 bg-brand-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="text-white">
              <h2 className="text-3xl font-bold mb-6">Get a Quote in {data.name}</h2>
              <p className="text-slate-300 text-lg mb-8">
                Tell us about your property maintenance requirements and we will get back to you within 24 hours.
              </p>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center">
                    <Phone className="w-6 h-6 text-brand-navy" />
                  </div>
                  <div>
                    <div className="text-slate-400 text-sm">Call us</div>
                    <a href={`tel:${companyInfo.phone}`} className="text-white font-bold text-lg hover:text-brand-yellow">
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center">
                    <Mail className="w-6 h-6 text-brand-navy" />
                  </div>
                  <div>
                    <div className="text-slate-400 text-sm">Email us</div>
                    <a href={`mailto:${companyInfo.email}`} className="text-white font-bold hover:text-brand-yellow">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-brand-navy" />
                  </div>
                  <div>
                    <div className="text-slate-400 text-sm">Covering</div>
                    <div className="text-white font-bold">{data.name} & {data.region}</div>
                  </div>
                </div>
              </div>
              <div className="mt-8 p-4 bg-slate-800 rounded-xl">
                <div className="text-sm text-slate-400 mb-2">Insurance & Guarantee</div>
                <div className="text-white font-medium">{companyInfo.insurance.publicLiability}</div>
                <div className="text-white font-medium">{companyInfo.guarantee}</div>
              </div>
            </div>

            <Card className="bg-white border-0">
              <CardContent className="p-6 sm:p-8">
                {isSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-brand-navy mb-2">Enquiry Received</h3>
                    <p className="text-brand-slate">We will be in touch within 24 hours.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <h3 className="text-xl font-bold text-brand-navy mb-4">Request a Quote</h3>
                    {submitError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                        Something went wrong. Please try again or call us directly.
                      </div>
                    )}
                    {/* Honeypot field */}
                    <input 
                      type="text" 
                      name="website" 
                      value={formData.honeypot}
                      onChange={(e) => setFormData({...formData, honeypot: e.target.value})}
                      style={{ display: 'none' }}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                    <div>
                      <label className="block text-sm font-medium text-brand-slate mb-1.5">Your Name *</label>
                      <Input 
                        required 
                        value={formData.name} 
                        onChange={(e) => setFormData({...formData, name: e.target.value})} 
                        placeholder="John Smith" 
                        className="h-12"
                        data-testid="location-input-name" 
                      />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-brand-slate mb-1.5">Email *</label>
                        <Input 
                          type="email" 
                          required 
                          value={formData.email} 
                          onChange={(e) => setFormData({...formData, email: e.target.value})} 
                          placeholder="john@email.com" 
                          className="h-12"
                          data-testid="location-input-email"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-brand-slate mb-1.5">Phone *</label>
                        <Input 
                          type="tel" 
                          required 
                          value={formData.phone} 
                          onChange={(e) => setFormData({...formData, phone: e.target.value})} 
                          placeholder="07xxx xxxxxx" 
                          className="h-12"
                          data-testid="location-input-phone"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-slate mb-1.5">Postcode / Area</label>
                      <Input 
                        value={formData.postcode} 
                        onChange={(e) => setFormData({...formData, postcode: e.target.value})} 
                        placeholder={`e.g., ${data.postcodes.split(',')[0]}`}
                        className="h-12"
                        data-testid="location-input-postcode"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-slate mb-1.5">What do you need help with? *</label>
                      <Textarea 
                        required 
                        value={formData.message} 
                        onChange={(e) => setFormData({...formData, message: e.target.value})} 
                        placeholder="Describe the work you need..." 
                        rows={4}
                        data-testid="location-textarea-message"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold h-12 text-lg" 
                      disabled={isSubmitting}
                      data-testid="location-submit-btn"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Enquiry'}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Other Locations */}
      <section className="py-12 bg-brand-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-lg font-semibold text-brand-navy mb-4 text-center">Other Areas We Serve</h3>
          <div className="flex flex-wrap justify-center gap-3">
            {otherLocations
              .map((loc) => (
                <a
                  key={loc.slug}
                  href={`/property-maintenance-${loc.slug}`}
                  className="px-4 py-2 bg-white border border-slate-200 hover:border-brand-yellow text-brand-slate hover:text-brand-navy rounded-full text-sm font-medium transition-colors"
                >
                  {loc.name}
                </a>
              ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p>© {new Date().getFullYear()} Expert Property Maintenance</p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <a href="/" className="hover:text-brand-yellow">Home</a>
              <a href="/landlord-services" className="hover:text-brand-yellow">Landlord Services</a>
              <a href="/letting-agent-maintenance" className="hover:text-brand-yellow">Letting Agents</a>
              <a href="/property-refurbishment" className="hover:text-brand-yellow">Refurbishment</a>
              <a href="/#contact" className="hover:text-brand-yellow">Contact</a>
            </div>
          </div>
        </div>
      </footer>
      <MobileActionBar phone={companyInfo.phone} />
    </div>
  );
};

export default LocationPage;
